import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Heading1, Heading2, Heading3, List, ListOrdered, Quote,
  Sparkles, Wand2, Minus, Plus as PlusIcon, Loader2,
} from "lucide-react";
import { mdToHtml, htmlToMd } from "./markdown";

const REWRITE_TONES = [
  { id: "professional", label: "Polish", icon: Wand2, hint: "Tighten and clean up" },
  { id: "casual", label: "Casual", icon: Sparkles, hint: "Friendlier voice" },
  { id: "authoritative", label: "Expert", icon: Sparkles, hint: "Authoritative, claim-heavy" },
  { id: "concise", label: "Shorter", icon: Minus, hint: "Make it tighter" },
  { id: "expand", label: "Expand", icon: PlusIcon, hint: "Add detail" },
];

/**
 * Tiptap rich-text editor with markdown round-trip and an inline
 * AI rewrite bubble menu.
 *
 * Props:
 *  - value           current markdown string (controlled by parent)
 *  - onChange(md)    fires on every editor change with the new markdown
 *  - projectId       used by the `/content/rewrite` API call
 *  - targetKeyword   passed to the rewrite endpoint so output stays on-topic
 *  - placeholder     placeholder text when empty
 *  - testId          data-testid for the wrapper (defaults to "rich-editor")
 *  - surfaceTestId   data-testid for the contenteditable surface (default "${testId}-surface")
 */
export default function RichEditor({
  value,
  onChange,
  projectId,
  targetKeyword,
  placeholder = "# Write your article in Markdown…",
  testId = "rich-editor",
  surfaceTestId,
}) {
  const lastEmittedRef = useRef(value || "");
  const [rewriting, setRewriting] = useState(false);

  const editor = useEditor({
    extensions: [
      // StarterKit v3 already includes Link — disable it here so we can configure
      // our own (openOnClick=false, autolink=true) without a duplicate warning.
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false }),
      Placeholder.configure({ placeholder }),
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: mdToHtml(value || ""),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[480px] font-mono-data text-sm leading-relaxed px-4 py-3",
        "data-testid": surfaceTestId || `${testId}-surface`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const md = htmlToMd(ed.getHTML());
      lastEmittedRef.current = md;
      onChange?.(md);
    },
  });

  // Sync external value -> editor (skipping the echo of our own onChange).
  useEffect(() => {
    if (!editor) return;
    if ((value || "") === lastEmittedRef.current) return;
    const html = mdToHtml(value || "");
    editor.commands.setContent(html, { emitUpdate: false });
    lastEmittedRef.current = value || "";
  }, [value, editor]);

  if (!editor) {
    return <div className="border border-foreground/30 min-h-[480px] flex items-center justify-center text-sm text-muted-foreground" data-testid={testId}>Loading editor…</div>;
  }

  const runRewrite = async (tone) => {
    if (!editor) return;
    const { from, to, empty } = editor.state.selection;
    if (empty) { toast.error("Select some text first"); return; }
    const selected = editor.state.doc.textBetween(from, to, "\n\n").trim();
    if (!selected) { toast.error("Selection is empty"); return; }
    setRewriting(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/content/rewrite`, {
        text: selected,
        target_keyword: targetKeyword || "",
        tone,
      });
      const replacementHtml = mdToHtml(data.content);
      editor.chain().focus().deleteRange({ from, to }).insertContent(replacementHtml).run();
      toast.success("Rewritten");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "AI rewrite failed");
    }
    setRewriting(false);
  };

  return (
    <div className="border border-foreground/30 bg-white" data-testid={testId}>
      {/* Sticky toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-foreground/15 bg-secondary/40 px-2 py-1" data-testid={`${testId}-toolbar`}>
        {[
          { active: editor.isActive("heading", { level: 1 }), onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(), Icon: Heading1, title: "H1", id: "h1" },
          { active: editor.isActive("heading", { level: 2 }), onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Icon: Heading2, title: "H2", id: "h2" },
          { active: editor.isActive("heading", { level: 3 }), onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(), Icon: Heading3, title: "H3", id: "h3" },
          { active: editor.isActive("bold"), onClick: () => editor.chain().focus().toggleBold().run(), Icon: Bold, title: "Bold", id: "bold" },
          { active: editor.isActive("italic"), onClick: () => editor.chain().focus().toggleItalic().run(), Icon: Italic, title: "Italic", id: "italic" },
          { active: editor.isActive("bulletList"), onClick: () => editor.chain().focus().toggleBulletList().run(), Icon: List, title: "Bullet list", id: "ul" },
          { active: editor.isActive("orderedList"), onClick: () => editor.chain().focus().toggleOrderedList().run(), Icon: ListOrdered, title: "Numbered list", id: "ol" },
          { active: editor.isActive("blockquote"), onClick: () => editor.chain().focus().toggleBlockquote().run(), Icon: Quote, title: "Quote", id: "quote" },
        ].map(({ active, onClick, Icon, title, id }) => (
          <button
            key={id}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onClick(); }}
            title={title}
            data-testid={`${testId}-tb-${id}`}
            className={`h-8 w-8 inline-flex items-center justify-center hover:bg-secondary transition-colors ${active ? "bg-foreground text-background" : ""}`}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        ))}
        <span className="ml-auto font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground pr-2">
          markdown · select text for AI rewrite
        </span>
      </div>

      {/* Bubble menu — AI rewrite + inline formatting */}
      <BubbleMenu
        editor={editor}
        options={{ placement: "top", offset: 8 }}
        shouldShow={({ editor: ed, from, to }) => {
          if (rewriting) return true;
          return from !== to && !ed.isActive("codeBlock");
        }}
      >
        <div className="flex items-center bg-foreground text-background border border-foreground shadow-lg" data-testid={`${testId}-bubble`}>
          <button type="button" onMouseDown={(e)=>{e.preventDefault(); editor.chain().focus().toggleBold().run();}} className={`h-8 w-8 inline-flex items-center justify-center hover:bg-background/10 ${editor.isActive("bold") ? "bg-background/15" : ""}`} title="Bold" data-testid={`${testId}-bubble-bold`}>
            <Bold className="w-3.5 h-3.5" />
          </button>
          <button type="button" onMouseDown={(e)=>{e.preventDefault(); editor.chain().focus().toggleItalic().run();}} className={`h-8 w-8 inline-flex items-center justify-center hover:bg-background/10 ${editor.isActive("italic") ? "bg-background/15" : ""}`} title="Italic" data-testid={`${testId}-bubble-italic`}>
            <Italic className="w-3.5 h-3.5" />
          </button>
          <span className="w-px h-5 bg-background/30 mx-0.5" />
          {REWRITE_TONES.map(({ id, label, icon: Icon, hint }) => (
            <button
              key={id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); runRewrite(id); }}
              disabled={rewriting}
              title={hint}
              data-testid={`${testId}-bubble-rewrite-${id}`}
              className="inline-flex items-center gap-1 px-2 h-8 text-[11px] font-mono-data uppercase tracking-widest hover:bg-background/10 disabled:opacity-50"
            >
              {rewriting
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Icon className="w-3 h-3" />} {label}
            </button>
          ))}
        </div>
      </BubbleMenu>

      <EditorContent editor={editor} />
    </div>
  );
}
